// useRedReal — estructura REAL de la red (confederaciones, federaciones,
// cooperativas) y sus conteos, desde el backend de CoopNet. Reemplaza los
// datos de ejemplo de mockData en las pantallas de estructura.
//
//   totales     → { confederaciones, federaciones, cooperativas, afiliados, paises }
//   conteos     → Maps id → { federaciones?, cooperativas?, afiliados } por nivel
//   bandera()   → emoji de bandera a partir del ISO2 del catálogo de geografía
//                 (no hay una lista de países escrita a mano)
//
// Las cifras salen de GET organizacion/resumen: cooperativas ACTIVAS y
// afiliados con estado 'activo'. Si algo no tiene respaldo en la base de
// datos, la pantalla debe mostrarlo vacío, no inventarlo.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { geografiaApi, organizacionApi } from '../services/coopnetApi';

const lista = (d) => (Array.isArray(d) ? d : []);
const VACIO = { internacionales: [], confederaciones: [], federaciones: [], cooperativas: [], resumen: null, paises: [] };
const TOTALES_VACIOS = { confederacionesInternacionales: 0, confederaciones: 0, federaciones: 0, cooperativas: 0, afiliados: 0, paises: 0, aporteMensual: 0 };

export default function useRedReal() {
  const [datos, setDatos] = useState(VACIO);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  const recargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const [ints, confs, feds, coops, resumen, paises] = await Promise.all([
        organizacionApi.listarInternacionales(),
        organizacionApi.listarConfederaciones(),
        organizacionApi.listarFederaciones(),
        organizacionApi.listarCooperativas(),
        organizacionApi.resumen(),
        geografiaApi.paises(true).catch(() => []), // solo para las banderas: si falla no rompe nada
      ]);
      setDatos({
        internacionales: lista(ints),
        confederaciones: lista(confs),
        federaciones: lista(feds),
        cooperativas: lista(coops),
        resumen,
        paises: lista(paises),
      });
    } catch (e) {
      setError(e?.message || 'No se pudo cargar la estructura de la red.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    recargar();
  }, [recargar]);

  const conteos = useMemo(() => {
    const indexar = (arr) => new Map(lista(arr).map((x) => [x.id, x]));
    return {
      internacionales: indexar(datos.resumen?.internacionales),
      confederaciones: indexar(datos.resumen?.confederaciones),
      federaciones: indexar(datos.resumen?.federaciones),
      cooperativas: indexar(datos.resumen?.cooperativas),
    };
  }, [datos.resumen]);

  const bandera = useCallback(
    (paisId, nombre) => {
      const p = datos.paises.find(
        (x) => (paisId != null && String(x.id) === String(paisId)) || (nombre && x.nombre?.toLowerCase() === String(nombre).toLowerCase())
      );
      const iso = p?.iso2;
      return iso && iso.length === 2 ? String.fromCodePoint(...[...iso.toUpperCase()].map((ch) => 127397 + ch.charCodeAt(0))) : '🏳️';
    },
    [datos.paises]
  );

  const nombrePais = useCallback((paisId) => datos.paises.find((x) => String(x.id) === String(paisId))?.nombre || '', [datos.paises]);

  return {
    internacionales: datos.internacionales,
    nombrePais,
    confederaciones: datos.confederaciones,
    federaciones: datos.federaciones,
    cooperativas: datos.cooperativas,
    totales: datos.resumen?.totales || TOTALES_VACIOS,
    conteos,
    cargando,
    error,
    recargar,
    bandera,
  };
}
